import { BUILTIN_MODELS, DEFAULT_SCALE_FORMAT, DeviceModel, DeviceRole, DeviceSetup, PairedDevice, ScaleBarcodeFormat } from '@iws/pos-devices'

import { meta, rows } from '@/db/database'
import { DeviceCatalogModel } from '@/http/models/DeviceCatalog'
import { currentDevice } from '@/lib/device'
import { storage } from '@/lib/storage'

/**
 * This till's devices (POS.md §18.9): kept on the device, backed up to `Terminal.device_setup` and restored from the
 * pulled terminal on a new or relinked PC. The catalog (System Admin) is kept in the device database so printing and
 * detection work offline.
 */
const SETUP_KEY = 'pos.devices'
const CATALOG_KEY = 'devices.catalog'

/** Until someone sets devices up, everything prints through the browser dialog (any printer the PC has). */
export const DEFAULT_SETUP: DeviceSetup = {
  devices: [{ id: 'browser', model_id: 'builtin:browser-a4', name: 'Browser print dialog', transport: 'browser' }],
  roles: { receipt: ['browser'], document: ['browser'], label: ['browser'] },
  options: { auto_print: false, open_drawer_on_cash: false },
}

let catalog: DeviceModel[] = []

export const loadCatalog = async () => {
  catalog = (await meta.get<DeviceModel[]>(CATALOG_KEY)) ?? []
  return catalog
}

/** Built-in generic models first, then the catalog's real ones. */
export const deviceModels = (): DeviceModel[] => [...BUILTIN_MODELS, ...catalog.filter((m) => !BUILTIN_MODELS.some((b) => b._id === m._id))]

/** After each sync, with the business settings. */
export const refreshCatalog = async () => {
  const res = await DeviceCatalogModel.getModels()
  if (!res.success || !res.data) return
  catalog = res.data.result
  await meta.set(CATALOG_KEY, catalog)
}

const listeners = new Set<() => void>()
export const onSetupChange = (fn: () => void) => {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

export const readSetup = (): DeviceSetup => storage.read<DeviceSetup | null>(SETUP_KEY, null) ?? DEFAULT_SETUP

/** Saves on the device now and backs it up to the terminal when online (failures leave the local copy). */
export const saveSetup = async (setup: DeviceSetup, backup: boolean) => {
  const next = { ...setup, updated_at: new Date().toISOString() }
  storage.write(SETUP_KEY, next)
  listeners.forEach((fn) => fn())
  const device = currentDevice()
  if (!backup || !device?.terminal_id) return { backedUp: false }
  const res = await DeviceCatalogModel.saveSetup(device.terminal_id, next)
  return { backedUp: res.success }
}

/**
 * Takes the terminal's saved setup (pulled with the terminal) when this PC has none, or when it is newer than the
 * local one: an edit in the admin (Settings → POS → Till devices) reaches the till this way. A local save that could
 * not be backed up stays newer and is kept.
 */
export const restoreSetupFromTerminal = async () => {
  const local = storage.read<DeviceSetup | null>(SETUP_KEY, null)
  const device = currentDevice()
  if (!device?.terminal_id) return false
  const terminal = await rows('terminals').where('_id').equals(device.terminal_id).first()
  const saved = terminal?.device_setup as DeviceSetup | null | undefined
  if (!saved?.updated_at) return false
  if (local && (local.updated_at ?? '') >= saved.updated_at) return false
  storage.write(SETUP_KEY, saved)
  listeners.forEach((fn) => fn())
  return true
}

export const newDeviceId = () => `dev-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`

export const withDevice = (setup: DeviceSetup, device: PairedDevice, roles: DeviceRole[]): DeviceSetup => {
  const nextRoles = { ...setup.roles }
  for (const role of roles) nextRoles[role] = [...(nextRoles[role] ?? []).filter((id) => id !== device.id), device.id]
  return { ...setup, devices: [...setup.devices.filter((d) => d.id !== device.id), device], roles: nextRoles }
}

export const withoutDevice = (setup: DeviceSetup, id: string): DeviceSetup => ({
  ...setup,
  devices: setup.devices.filter((d) => d.id !== id),
  roles: Object.fromEntries(Object.entries(setup.roles).map(([role, ids]) => [role, (ids ?? []).filter((d) => d !== id)])),
})

/**
 * Scale label barcode formats (POS.md §18.9): those of the models paired on this till (a label-printing scale is added
 * with the `scale_labels` role), else the default `21` + PLU + grams. Only paired models count, so a catalog format
 * this shop never uses cannot swallow an ordinary barcode.
 */
export const scaleFormats = (): ScaleBarcodeFormat[] => {
  const models = deviceModels()
  const formats = readSetup().devices.flatMap((d) => models.find((m) => m._id === d.model_id)?.scale?.barcode_formats ?? [])
  return formats.length ? formats : [DEFAULT_SCALE_FORMAT]
}
